import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, useIsAuthenticated } from '../store/auth'
import { requestOTP, verifyOTP } from '../api/auth'

type Step = 'email' | 'code'

const RESEND_COOLDOWN_SECONDS = 30

export function LoginPage() {
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()
  const setSession = useAuthStore((s) => s.setSession)

  // Redirect already-authenticated users away from /login immediately.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Resend cooldown state
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  // Auto-focus the code input when entering the code step.
  const codeInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (step === 'code') {
      codeInputRef.current?.focus()
    }
  }, [step])

  const handleRequestOTP = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    setError(null)
    setIsSubmitting(true)
    try {
      await requestOTP(trimmed)
      setStep('code')
      setCode('')
      startCooldown()
    } catch {
      setError('Could not send code. Check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [email, startCooldown])

  const handleVerifyOTP = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = code.trim()
    if (trimmed.length !== 6) return
    setError(null)
    setIsSubmitting(true)
    try {
      const { token, user } = await verifyOTP(email.trim(), trimmed)
      setSession({ token, user })
      navigate('/', { replace: true })
    } catch (err: unknown) {
      const apiErr = err as { error?: { code?: string; message?: string } }
      if (apiErr?.error?.code === 'invalid_code') {
        setError('Invalid or expired code. Try again.')
      } else {
        setError('Verification failed. Please try again.')
      }
      setCode('')
      codeInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }, [code, email, navigate, setSession])

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Accept only digits, max 6
    const val = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(val)
    setError(null)
    // Auto-submit once 6 digits are entered
    if (val.length === 6) {
      // Defer to let state settle before submitting
      setTimeout(() => {
        handleVerifyOTP()
      }, 0)
    }
  }, [handleVerifyOTP])

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return
    setError(null)
    setCode('')
    setIsSubmitting(true)
    try {
      await requestOTP(email.trim())
      startCooldown()
    } catch {
      setError('Could not resend code. Try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [resendCooldown, email, startCooldown])

  // Don't render the form while checking auth state
  if (isAuthenticated) return null

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: 'var(--color-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
          padding: '40px 36px 36px',
        }}
      >
        {step === 'email' ? (
          <EmailStep
            email={email}
            onEmailChange={(v) => { setEmail(v); setError(null) }}
            onSubmit={handleRequestOTP}
            isSubmitting={isSubmitting}
            error={error}
          />
        ) : (
          <CodeStep
            email={email.trim()}
            code={code}
            onCodeChange={handleCodeChange}
            onVerify={handleVerifyOTP}
            onBack={() => { setStep('email'); setCode(''); setError(null) }}
            onResend={handleResend}
            isSubmitting={isSubmitting}
            error={error}
            resendCooldown={resendCooldown}
            codeInputRef={codeInputRef}
          />
        )}
      </div>
    </div>
  )
}

// ── Email step ──────────────────────────────────────────────────────────────

interface EmailStepProps {
  email: string
  onEmailChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
  isSubmitting: boolean
  error: string | null
}

function EmailStep({ email, onEmailChange, onSubmit, isSubmitting, error }: EmailStepProps) {
  return (
    <form onSubmit={onSubmit}>
      <h1 className="display-serif" style={{ fontSize: '2rem', marginBottom: '28px' }}>
        Sign in to todux
      </h1>

      <label
        htmlFor="login-email"
        className="eyebrow"
        style={{ display: 'block', marginBottom: '8px' }}
      >
        Email
      </label>
      <input
        id="login-email"
        type="email"
        autoComplete="email"
        autoFocus
        required
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        disabled={isSubmitting}
        placeholder="you@example.com"
        style={inputStyle}
      />

      {error && <p style={errorStyle}>{error}</p>}

      <button type="submit" disabled={isSubmitting || !email.trim()} style={primaryBtnStyle}>
        {isSubmitting ? 'Sending…' : 'Send code'}
      </button>
    </form>
  )
}

// ── Code step ───────────────────────────────────────────────────────────────

interface CodeStepProps {
  email: string
  code: string
  onCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onVerify: (e?: React.FormEvent) => void
  onBack: () => void
  onResend: () => void
  isSubmitting: boolean
  error: string | null
  resendCooldown: number
  codeInputRef: React.RefObject<HTMLInputElement>
}

function CodeStep({
  email,
  code,
  onCodeChange,
  onVerify,
  onBack,
  onResend,
  isSubmitting,
  error,
  resendCooldown,
  codeInputRef,
}: CodeStepProps) {
  return (
    <form onSubmit={onVerify}>
      <h1 className="display-serif" style={{ fontSize: '2rem', marginBottom: '8px' }}>
        Check your email
      </h1>
      <p
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '0.9375rem',
          color: 'var(--color-ink-muted)',
          marginBottom: '28px',
          lineHeight: 1.5,
        }}
      >
        We sent a 6-digit code to{' '}
        <strong style={{ color: 'var(--color-ink)' }}>{email}</strong>.
      </p>

      <label htmlFor="login-code" className="eyebrow" style={{ display: 'block', marginBottom: '8px' }}>
        Code
      </label>
      <input
        id="login-code"
        ref={codeInputRef}
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        autoComplete="one-time-code"
        value={code}
        onChange={onCodeChange}
        disabled={isSubmitting}
        placeholder="000000"
        style={{
          ...inputStyle,
          fontFamily: 'var(--font-mono)',
          fontSize: '1.5rem',
          textAlign: 'center',
          letterSpacing: '0.25em',
        }}
      />

      {error && <p style={errorStyle}>{error}</p>}

      <button
        type="submit"
        disabled={isSubmitting || code.length !== 6}
        style={{ ...primaryBtnStyle, marginBottom: '12px' }}
      >
        {isSubmitting ? 'Verifying…' : 'Verify'}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button type="button" onClick={onBack} style={ghostBtnStyle}>
          ← Back
        </button>

        <button
          type="button"
          onClick={onResend}
          disabled={resendCooldown > 0 || isSubmitting}
          style={ghostBtnStyle}
        >
          {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
        </button>
      </div>
    </form>
  )
}

// ── Shared styles ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.9375rem',
  color: 'var(--color-ink)',
  background: 'var(--color-paper-dark)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  marginBottom: '20px',
  boxSizing: 'border-box',
}

const primaryBtnStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '11px 16px',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: 'var(--color-paper)',
  background: 'var(--color-ink)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  opacity: 1,
}

const ghostBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '4px 0',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.875rem',
  color: 'var(--color-ink-muted)',
  cursor: 'pointer',
  transition: 'color 0.1s',
}

const errorStyle: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontSize: '0.875rem',
  color: 'var(--color-prio-high)',
  marginBottom: '14px',
  lineHeight: 1.4,
}
